"use client";

import { useEffect, useRef, useState } from "react";

/**
 * ParticleNumber — efecto de inicio.
 *
 * Renderiza el texto objetivo en un canvas offscreen, samplea puntos opacos
 * como targets, y anima partículas desde posiciones aleatorias hasta formar
 * las cifras. Una vez estabilizado, hace fade-out y revela el texto DOM
 * real (que sigue actualizándose con el contador en vivo).
 *
 * Respeta `prefers-reduced-motion`.
 */

interface Props {
  text: string;
  liveText: string;
  active: boolean;
  className?: string;
  /** Aplica gradient-text al live text (clase del proyecto) */
  gradient?: boolean;
}

export default function ParticleNumber({
  text,
  liveText,
  active,
  className = "",
  gradient = false,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLSpanElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [phase, setPhase] = useState<"idle" | "animating" | "done">("idle");
  const [reducedMotion, setReducedMotion] = useState<boolean>(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false,
  );

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!active) return;
    if (phase !== "idle") return;
    const next: "animating" | "done" = reducedMotion ? "done" : "animating";
    queueMicrotask(() => setPhase(next));
  }, [active, phase, reducedMotion]);

  useEffect(() => {
    if (phase !== "animating") return;
    const canvas = canvasRef.current;
    const measureEl = measureRef.current;
    const container = containerRef.current;
    if (!canvas || !measureEl || !container) return;

    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const measureRect = measureEl.getBoundingClientRect();

    const width = Math.ceil(measureRect.width) + 40;
    const height = Math.ceil(measureRect.height) + 20;

    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const computed = window.getComputedStyle(measureEl);
    const fontPx = parseFloat(computed.fontSize) || 64;
    const fontFamily = computed.fontFamily || "sans-serif";
    const fontWeight = computed.fontWeight || "400";
    const fontStyle = computed.fontStyle || "normal";

    const textCanvas = document.createElement("canvas");
    textCanvas.width = canvas.width;
    textCanvas.height = canvas.height;
    const tctx = textCanvas.getContext("2d");
    if (!tctx) return;
    tctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    tctx.fillStyle = "#ffffff";
    tctx.font = `${fontStyle} ${fontWeight} ${fontPx}px ${fontFamily}`;
    tctx.textBaseline = "middle";
    tctx.textAlign = "center";
    tctx.fillText(text, width / 2, height / 2);

    const STEP = Math.max(3, Math.round(fontPx / 22));
    const imgData = tctx.getImageData(
      0,
      0,
      textCanvas.width,
      textCanvas.height,
    );
    const targets: Array<{ x: number; y: number }> = [];
    for (let y = 0; y < height; y += STEP) {
      for (let x = 0; x < width; x += STEP) {
        const px = Math.floor(x * dpr);
        const py = Math.floor(y * dpr);
        const idx = (py * textCanvas.width + px) * 4 + 3;
        if (imgData.data[idx] > 128) {
          targets.push({ x, y });
        }
      }
    }

    if (targets.length === 0) {
      queueMicrotask(() => setPhase("done"));
      return;
    }

    interface Particle {
      x: number;
      y: number;
      tx: number;
      ty: number;
      size: number;
      hue: number;
    }

    const particles: Particle[] = targets.map((t) => {
      const angle = Math.random() * Math.PI * 2;
      const distance = 80 + Math.random() * 220;
      return {
        x: t.x + Math.cos(angle) * distance,
        y: t.y + Math.sin(angle) * distance,
        tx: t.x,
        ty: t.y,
        size: 1.2 + Math.random() * 1.2,
        hue: Math.random(),
      };
    });

    const TOTAL_DURATION = 1700;
    const SETTLED_FADE_DURATION = 600;
    const startTime = performance.now();
    let rafId = 0;

    function tick(now: number) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / TOTAL_DURATION, 1);
      const ease = 1 - Math.pow(1 - progress, 3);

      ctx!.clearRect(0, 0, width, height);

      for (const p of particles) {
        p.x = p.x + (p.tx - p.x) * (0.04 + ease * 0.06);
        p.y = p.y + (p.ty - p.y) * (0.04 + ease * 0.06);

        const r = Math.round(0 + 41 * p.hue);
        const g = Math.round(217 - 35 * p.hue);
        const b = Math.round(196 + 50 * p.hue);
        const alpha = 0.55 + 0.35 * ease;
        ctx!.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`;
        ctx!.beginPath();
        ctx!.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx!.fill();
      }

      if (progress < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        const fadeStart = performance.now();
        const fadeOut = (n: number) => {
          const t = Math.min((n - fadeStart) / SETTLED_FADE_DURATION, 1);
          ctx!.clearRect(0, 0, width, height);
          for (const p of particles) {
            const r = Math.round(0 + 41 * p.hue);
            const g = Math.round(217 - 35 * p.hue);
            const b = Math.round(196 + 50 * p.hue);
            ctx!.fillStyle = `rgba(${r}, ${g}, ${b}, ${0.9 * (1 - t)})`;
            ctx!.beginPath();
            ctx!.arc(p.x, p.y, p.size, 0, Math.PI * 2);
            ctx!.fill();
          }
          if (t < 1) {
            rafId = requestAnimationFrame(fadeOut);
          } else {
            ctx!.clearRect(0, 0, width, height);
            queueMicrotask(() => setPhase("done"));
          }
        };
        rafId = requestAnimationFrame(fadeOut);
      }
    }

    rafId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [phase, text]);

  // The textual content uses the same font/weight by sharing className
  // on every span. Crucially, gradient-text MUST be on the actual text
  // span (not the wrapper) — otherwise the child text inherits
  // text-fill-color: transparent without the parent's background and
  // becomes invisible after the canvas fades out.
  const textClass = gradient ? `${className} gradient-text` : className;

  return (
    <span
      ref={containerRef}
      className="relative inline-block align-baseline"
    >
      {/* Layout-establishing measure (invisible, takes space) */}
      <span
        ref={measureRef}
        aria-hidden="true"
        className={`${textClass} invisible whitespace-nowrap`}
      >
        {text}
      </span>

      {/* Animated canvas overlay during animation */}
      {phase === "animating" && (
        <canvas
          ref={canvasRef}
          aria-hidden="true"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
        />
      )}

      {/* Live text — rendered always, fades in once animation finishes */}
      <span
        aria-label={liveText}
        className={`${textClass} absolute inset-0 flex items-center justify-center transition-opacity duration-500 whitespace-nowrap ${
          phase === "done" ? "opacity-100" : "opacity-0"
        }`}
      >
        {liveText}
      </span>
    </span>
  );
}
